import React from "react";
import {
  Building2,
  ShieldAlert,
  Landmark,
  Sliders,
  ClipboardCheck,
  BarChart3,
  FlaskConical,
  ScrollText,
  Bell,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Map,
} from "lucide-react";
import RadialNavigator from "../../components/navigation/RadialNavigator";

interface AdminDashboardProps {
  activeMenu: string;
  onNavigate?: (menuId: string) => void;
}

/* ── Status row — neutral ── */
type DS = "ok" | "warn" | "err";
const SRow: React.FC<{ label: string; s: DS }> = ({ label, s }) => {
  const cfg: Record<DS, { icon: React.ReactNode; cls: string }> = {
    ok: { icon: <CheckCircle2 size={11} />, cls: "text-emerald-400" },
    warn: { icon: <AlertCircle size={11} />, cls: "text-amber-400" },
    err: { icon: <XCircle size={11} />, cls: "text-rose-400" },
  };
  return (
    <div className="flex items-center gap-2 py-[3px]">
      <span className={cfg[s].cls}>{cfg[s].icon}</span>
      <span className="text-[11px] text-gray-500 leading-snug">{label}</span>
    </div>
  );
};

/* ── Muted card ── */
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="flex flex-col overflow-hidden rounded-xl"
    style={{
      background: "#fff",
      border: "1px solid #eaecf0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}
  >
    {children}
  </div>
);

const MockBadge = () => (
  <span className="status-badge xs warning">
    MOCK
  </span>
);

/* ── Card header — colorful icon + title ── */
const CHead: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  badge?: React.ReactNode;
}> = ({ icon, title, color, badge }) => (
  <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
    <div className="flex items-center gap-1.5">
      <span className={color}>{icon}</span>
      <span
        className={`text-[9.5px] font-bold uppercase tracking-widest ${color}`}
      >
        {title}
      </span>
    </div>
    <div className="flex items-center gap-1">
      {badge}
      <MockBadge />
    </div>
  </div>
);

/* ── Colored badge ── */
type BadgeVariant = "success" | "danger" | "warning" | "info" | "neutral";
const Badge: React.FC<{ label: string; variant: BadgeVariant }> = ({
  label,
  variant,
}) => (
  <span className={`status-badge xs ${variant}`}>
    {label}
  </span>
);

/* ══ AdminDashboard ══ */
const LeadershipDashboardContent: React.FC<AdminDashboardProps> = ({
  activeMenu,
  onNavigate,
}) => (
  <div
    className="grid gap-3 p-2 overflow-hidden scroll-container"
    style={{
      height: "calc(100vh - 140px)",
      gridTemplateColumns: "1.9fr 1fr 1fr 1fr",
      gridTemplateRows: "1fr 1fr 1fr",
      background: "#f7f8fc",
    }}
  >
    {/* ══ COL 1 – Site Map hero panel ══ */}
    <div
      className="flex flex-col overflow-hidden rounded-2xl"
      style={{
        gridRow: "span 3",
        background:
          "linear-gradient(160deg, #faf9ff 0%, #f3f0ff 60%, #ede9fe 100%)",
        border: "1.5px solid #ddd6fe",
        boxShadow:
          "0 4px 24px rgba(109,40,217,0.10), 0 1px 4px rgba(109,40,217,0.06)",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2 flex-shrink-0">
        <div
          className="flex items-center justify-center rounded-lg"
          style={{
            width: 26,
            height: 26,
            background: "linear-gradient(135deg,#7c3aed,#a855f7)",
          }}
        >
          <Map size={13} color="#fff" />
        </div>
        <span className="text-[11px] font-bold text-violet-700 uppercase tracking-widest">
          Site Map
        </span>
      </div>
      {/* Divider */}
      <div
        style={{
          height: 1,
          background: "linear-gradient(to right, #ddd6fe, transparent)",
          margin: "0 16px",
        }}
      />
      {/* Navigator */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden"
        style={{ minHeight: 0 }}
      >
        <RadialNavigator
          className="compact expanded"
          radius={185}
          activeId={activeMenu === "dashboard" ? "organisation" : activeMenu}
          onSelect={(id) => {
            onNavigate?.(id);
          }}
        />
      </div>
    </div>

    {/* ══ ROW 1: Organisation | Risk Summary | Governance ══ */}

    {/* Organisation */}
    <Card>
      <CHead
        icon={<Building2 size={11} />}
        title="Organisation"
        color="text-blue-500"
      />
      <div className="flex-1 flex items-center justify-around px-3 pb-3 gap-1">
        {[
          ["15", "Depts"],
          ["247", "People"],
          ["8", "Policies"],
        ].map(([v, l]) => (
          <div key={l} className="flex flex-col items-center">
            <span className="text-xl font-black text-gray-700">{v}</span>
            <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">
              {l}
            </span>
          </div>
        ))}
      </div>
    </Card>

    {/* Risk Summary */}
    <Card>
      <CHead
        icon={<ShieldAlert size={11} />}
        title="Risk Summary"
        color="text-rose-500"
        badge={<Badge label="3 CRITICAL" variant="danger" />}
      />
      <div className="flex-1 flex flex-col justify-center gap-1.5 px-3 pb-3">
        {[
          { l: "High", n: 3, max: 25, bar: "#f87171" },
          { l: "Medium", n: 12, max: 25, bar: "#fbbf24" },
          { l: "Low", n: 25, max: 25, bar: "#34d399" },
        ].map(({ l, n, max, bar }) => (
          <div key={l} className="flex items-center gap-2">
            <span className="text-[9.5px] font-semibold w-10 text-right text-gray-500">
              {l}
            </span>
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 rounded-full"
                style={{ width: `${(n / max) * 100}%`, background: bar }}
              />
            </div>
            <span className="text-[10px] font-bold w-4 text-center text-gray-600">
              {n}
            </span>
          </div>
        ))}
      </div>
    </Card>

    {/* Governance */}
    <Card>
      <CHead
        icon={<Landmark size={11} />}
        title="Governance"
        color="text-violet-500"
        badge={<Badge label="ACTIVE" variant="info" />}
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
        <SRow s="ok" label="Board Meeting: Next week" />
        <SRow s="warn" label="Policy Review: Overdue" />
        <SRow s="ok" label="Compliance: Up to date" />
      </div>
    </Card>

    {/* ══ ROW 2: Controls Health | Compliance | KPI ══ */}

    {/* Controls Health */}
    <Card>
      <CHead
        icon={<Sliders size={11} />}
        title="Controls Health"
        color="text-indigo-500"
        badge={
          <Badge label="85% EFFECTIVE" variant="info" />
        }
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
        <SRow s="ok" label="Preventive: 90% effective" />
        <SRow s="ok" label="Detective: 82% effective" />
        <SRow s="warn" label="Corrective: 74% effective" />
      </div>
    </Card>

    {/* Compliance */}
    <Card>
      <CHead
        icon={<ClipboardCheck size={11} />}
        title="Compliance"
        color="text-teal-500"
        badge={<Badge label="92% SCORE" variant="success" />}
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
        <SRow s="ok" label="SOX Monitoring Active" />
        <SRow s="warn" label="ISO Audit Due Soon" />
        <SRow s="ok" label="GDPR Controls Healthy" />
      </div>
    </Card>

    {/* KPI Snapshot */}
    <Card>
      <CHead
        icon={<BarChart3 size={11} />}
        title="KPI Snapshot"
        color="text-emerald-500"
      />
      <div className="flex-1 flex items-center gap-2 px-3 pb-3">
        {[
          {
            v: "94%",
            l: "On-Time",
            bg: "#f0fdf4",
            val: "#16a34a",
            icon: <TrendingUp size={9} color="#16a34a" />,
          },
          {
            v: "12",
            l: "Open",
            bg: "#eff6ff",
            val: "#2563eb",
            icon: <TrendingDown size={9} color="#2563eb" />,
          },
          {
            v: "4",
            l: "Overdue",
            bg: "#fff7ed",
            val: "#ea580c",
            icon: <AlertCircle size={9} color="#ea580c" />,
          },
        ].map(({ v, l, bg, val, icon }) => (
          <div
            key={l}
            className="flex-1 flex flex-col items-center justify-center rounded-lg py-2"
            style={{ background: bg }}
          >
            <span
              className="text-base font-black leading-tight"
              style={{ color: val }}
            >
              {v}
            </span>
            <div className="flex items-center gap-0.5 mt-0.5">
              {icon}
              <span
                className="text-[8.5px] font-semibold uppercase tracking-wide"
                style={{ color: val }}
              >
                {l}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {/* ══ ROW 3: Testing | Audit Log | Notifications ══ */}

    {/* Testing Progress */}
    <Card>
      <CHead
        icon={<FlaskConical size={11} />}
        title="Testing Progress"
        color="text-purple-500"
        badge={<Badge label="Q3 ACTIVE" variant="info" />}
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-1.5">
        <div>
          <div className="flex justify-between text-[9.5px] mb-1">
            <span className="text-gray-400 font-medium">
              Overall completion
            </span>
            <span className="font-bold text-gray-600">78%</span>
          </div>
          <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-1.5 rounded-full"
              style={{ width: "78%", background: "#a78bfa" }}
            />
          </div>
        </div>
        <SRow s="warn" label="Evidence Pending: 5 items" />
        <SRow s="ok" label="Deficiencies Open: 2 minor" />
      </div>
    </Card>

    {/* Audit Log */}
    <Card>
      <CHead
        icon={<ScrollText size={11} />}
        title="Audit Log"
        color="text-sky-500"
        badge={<Badge label="LIVE" variant="info" />}
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
        {[
          ["User role updated", "2m ago"],
          ["Policy ACK recorded", "15m ago"],
          ["New incident filed", "1h ago"],
        ].map(([msg, t]) => (
          <div key={msg} className="flex items-center justify-between py-[3px]">
            <div className="flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
              <span className="text-[11px] text-gray-500">{msg}</span>
            </div>
            <span className="text-[9.5px] text-gray-400 flex-shrink-0 ml-2">
              {t}
            </span>
          </div>
        ))}
      </div>
    </Card>

    {/* Notifications */}
    <Card>
      <CHead
        icon={<Bell size={11} />}
        title="Notifications"
        color="text-amber-500"
        badge={<Badge label="1 URGENT" variant="warning" />}
      />
      <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
        <SRow s="warn" label="Policy Review Overdue" />
        <SRow s="ok" label="New User Added" />
        <SRow s="ok" label="Control Test Passed" />
      </div>
    </Card>
  </div>
);

export default LeadershipDashboardContent;
